<template>
<div class="chapter">
<div class="inner_page">
<p class="C118_27"><a id="page_3"></a>I’M SO ANGRY I MADE A SIGN<sup><a id="id_16"></a><span href="#/xhtml/10_Note#id_17" title="Link target outside of Sample">1</span></sup></p>
<p class="C116_21">Michael Taussig</p>
<p class="C159_22">A NOTE ON FORM</p>
<p class="P1">I have inserted the signs in Zuccotti Park as set-apart quotations in the
            center of the page. And sometimes I have also inserted quotes from texts by
            philosophers, poets, and other people worth listening to. They, too, look like signs. I
            don’t think you will confuse them, but it’s better if you do.</p>
<p class="C151_18">A NOTE ON STRATEGY</p>
<p class="P1">Nietzsche says somewhere that a historian has to create a text equal to what
            is being written about. This would seem especially compelling when it comes to Occupy
            Wall Street.</p>
<p class="P26">In <em>The Gay Science,</em> Nietzsche has a paragraph, “To Destroy
            Only as Creators,” which I take to mean a demand not for “positive
            critique,” but that we be aware of how description and analysis of an event is a
            culture-creating activity, and write accordingly.</p>
<p class="P26">Coming back to this text of mine six months after it was written is like
            visiting a strange and fabulous land. I imagine it will be the same for you.</p>
<p class="C160p"><em>Wall St is everywhere<br/> therefore we have to occupy
            everywhere</em></p>
<p class="C130_23t"><a id="page_4"></a>11:00 P.M., OCTOBER 13, 2011</p>
<p class="C130">On my way downtown to Occupy Wall Street, Zuccotti Park, New York City.
            Flustered and excited. E-mails coming in from Yesenia, and from Michelle and Alex in my
            sorcery and magic class at Columbia. They should be writing their weekly assignments for
            school. They are so far behind. But this is the night the mayor will attack. I stop by
            the bagel store to tell this to my Mexican friend who serves behind the counter. He is
            counting money and is preoccupied. He has never heard of OWS and he tries to look
            interested. My canvas bag is stuffed with sleeping bags for Saa and myself. Long wait
            for the #1 train. Unbearable. Alex says rumors of police closing in at midnight. Danny
            Alonso, also in my sorcery class, once compared visiting Zuccotti Park—which he
            did all the time from day one—to the excitement of going to the movies and
            getting into the trance of that other reality. You get hooked, he later wrote. “I
            would be hypnotized and turned into someone else.” In fact, many selves. A
            drumming self. A facilitator self. A hunting and gathering self roaming Manhattan for
            tarpaulins and food from dumpsters to bring the tribe, listening to stories “and
            healing from people who had come from all over to share in this moment.” Many of
            these people had lost their jobs.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Lost my job but found an occupation</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">You break through the screen, like Alice in Wonderland. And now you
            can’t leave or do without it. Everything else seems fake and boring. So how do
            you write about it? In such circumstances of dissolving norms, effervescent atmosphere,
            invention and reinvention, what happens to the ethnographer’s magic—as
            Malinowski called it—and that old standby of “participant
            observation?”</p>
<p class="P26"><a id="page_5"></a>And is the magic strong enough?</p>
<p class="P26">Am I clear here? I don’t think so, and I think this is the problem of
            writing surprise and writing strangeness, surely the dilemma and sine qua non of
            ethnography? As soon as you write surprise—or, rather, attempt to write
            it—it is as if the surprise has been made digestible so it is no longer
            surprising, no longer strange. To “occupy ethnography” is to get around
            that somehow, to seize on the means and manner of representation as estranged. An
            exuberant style is not enough. That is why I so much like the zombie-style bodies and
            faces of the sign holders who populate Zuccotti Park, graven images outside time.</p>
<p class="C160"><img alt="" class="Image85" src="img/22_1.png"/></p>
<p class="C160"><a id="page_6"></a><img alt="" class="Image83" src="img/23_1.png"/></p>
<p class="C160"><img alt="" class="Image87" src="img/23_2.png"/></p>
<p class="C160"><a id="page_7"></a><img alt="" class="Image83" src="img/24_1.png"/></p>
<p class="C160"><a id="page_8"></a><img alt="" class="Image83" src="img/25_1.png"/></p>
<p class="C164"><a id="page_9"></a><img alt="" class="Image83" src="img/26_1.png"/></p>
<p class="P26">Welcome to Hakim Bey’s Temporary Autonomous Zone. I recall Paris, May
            1968: people said they lived in that zone for months, didn’t sleep, didn’t
            need to. Out of nowhere a community forms, fueled by the unforeseen chance to fight
            back. Decades drift away. Decades of Fox News and Goldman Sachs. Decades of gutting what
            was left of the social contract. Decades in which kids came to think being a banker was
            sexy. When that happens, you <em>know</em> it’s all over—or about to
            explode, as once again history throws a curveball. Once in a lifetime, the unpredictable
            occurs and reality gets redefined.</p>
<p class="P26">The most striking sign I have seen at Zuccotti Park over three months was a
            life-size painting of a man’s striped tie on a white background. The tie was
            knotted to form a circle at the top like a hangman’s noose. Wordless. Next to it
            was a sign with blotchy patches of white over some of the letters:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong><a id="page_10"></a>They piss on us and call<br/> it trickle
                down</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">America wakes up from the American Dream. “I’ve been waiting for
            this all my life,” says Craig, who stayed with me overnight from California with
            naught but a backpack on his way to Zuccotti Park.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>I awoke in a sweat<br/> from the American Dream</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">“At night we lie all together on the concrete,” writes Alex,
            “a few sleeping, the rest talking in low voices, or reading next to the street
            lights, or cursing the constant sirens that we are certain the NYPD sends around the
            park at night just to keep us poorly rested and easily dominated, or looking through the
            thin canopy of leaves between the dark towers and the sky. The first morning we all
            agreed that we felt as if those buildings would fall in on us.</p>
<p class="P26">“Dear WB,” she goes on, “maybe OWS is something like
            that awakening that is between sleep and consciousness. We are emerging from slumber but
            we are disoriented, stupored, caught between the dream logic of capitalism and the newly
            forming world.”</p>
<p class="P26">“Dear WB.” How blessed is that? She is writing code, of
            course—direct from the state of emergency. She is searching the zone of the
            dialectical image that Walter Benjamin envisaged as emerging from the dream sleep of
            capitalism that reactivated mythic powers. Just as one swims in the surreal zone of
            semisleep as harbinger of revolution, so does the epoch. Does the new security state
            understand and believe this too, along with <a id="page_11"></a>Walter? Why else would
            they walk silently through the park at night, filming the sleepers?</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>you must be asleep to experience<br/> the American
            Dream</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Salomeya put it a little differently. She has a theory, as usual. Working out
            of the sense of the body and magic she finds in Malinowski’s discussions of clan
            and sub-clan solidarity and sorcery, she discerns a form of human bonding relevant to
            OWS that she calls “erotic materialism.” It is a brilliant rereading of
            classical anthropology applied as much to Zuccotti Park as to aforesaid dream sleep
            mythology. (Now she tells me she suffers from being too abstract, but there’s
            little she can do about it.)</p>
<p class="P26">But the lines get blurred. Solidarity gets tested. As time goes by, it is
            said that undercover police roam the park disguised as protesters. (Question: What does
            a protester look like?) It is said that homeless people are being directed by the police
            and shelters to go to Zuccotti Park in the hope that they will dilute and factionalize
            the occupation. The ideals of the radical hipsters from Brooklyn with their web-savvy
            culture are being tested like never before by these homeless men who seem uninterested
            in what the hipsters stand for, yet the whole point of OWS is homelessness. As time goes
            by—horror of horrors!—something like property and real estate interests
            surface. Someone quips that there is an Upper East Side section of tents in the park,
            and one hears muttering of gentrification, as if this utopic space is reproducing what
            it is against.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>We just bought real estate<br/> in your mind</strong></p>
<p class="line1">.   .   .</p>
<p class="P1"><a id="page_12"></a>It is said that there are rapes and stealing, and there
            certainly is stealing. Craig got all his stuff swiped after he left for half an hour to
            wash up in the bathroom of Trinity Church.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>I can hire one half of the<br/> working class<br/> to kill the
                other
                <br/>                        Jay
                Gould</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">I walk out of the subway at Fulton Street into the canyons of Wall Street,
            Fritz Lang’s Metropolis of soaring towers holding up a black sky heavy with rain
            clouds, workers in cages like moles—no speech (1927), only cryptic subtitles and
            madly gesticulating figures with pasty white expressionist faces caught in frozen
            grimace. Police cars and vans are everywhere around the park and secreted in back
            alleys.</p>
<p class="P26">Down on the ground it is a war zone crackling with expectancy. But overhead,
            Freedom Tower, sheathed in mirrors, dwarfs everything, glistening with blue light. What
            did Benjamin say in “This Space for Rent” in <em>One Way Street</em>, that
            other OWS published as the fuse was being lit in Europe, in 1928, one year after
                <em>Metropolis</em>:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>What, in the end, makes advertisements<br/> superior to criticism?
                Not what the<br/> moving red neon says—but the fiery<br/> pool reflecting
                it in the asphalt</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">You take a deep breath when you get there, and you can’t breathe again
            until you leave. It is devastatingly spectacular and <a id="page_14"></a>inhuman: the
            architecture of what Marx called M-M’, meaning money making money, meaning
            finance capital, of which credit default swaps are the ultimate expression of the
            moneylenders Christ drove from the temple.</p>
<p class="C160"><a id="page_13"></a><img alt="" class="Image83" src="img/30_1.png"/></p>
<p class="C164"><img alt="" class="Image83" src="img/31_1.png"/></p>
<p class="P26">Is this what occupation of the park means—a moral movement against the
            exploitation of people not only by the moneylenders, but by the apparently neutral means
            of money doing <a id="page_15"></a>it all on its own, meaning M-M’? Does the
            occupation occupy the magical energy of this fetish, and from this abundant source draw
            its energy?</p>
<p class="P26">Wall Street is forbiddingly allegorical. Fritz Lang provides a frightening
            topography of heaven and hell, of our Metropolis. But, closer to home, so did Diego
            Rivera in 1931, during the Great Depression, with his painting <em>Fondos
                Congelados</em> (Frozen Assets), showing the serene temples of Manhattan as an
            archaelogical stratum atop a dimly lit subterranean morgue with corpses laid out in
            rows, supervised by a lonely guard. Perhaps they are not corpses, you say to yourself,
            but merely sleeping bodies. These are the “frozen assets”: the unemployed
            laid out like corpses in prisonlike dwellings, bringing to mind the notion of capital as
            “congealed labor.” It is a terrible picture. You could hear a pin
            drop.</p>
<p class="P26">But now, miraculously, with the occupation in full swing, the picture had
            come alive as the architecture of M-M’ lost its grip. We looked at each other
            eye-to-eye in those days, never quite knowing what the next enchanted moment would
            bring. We were bigger than the buildings, and instead of being physically compressed and
            mentally scripted, like the poor bastards in the offices all around us, we lived moment
            by moment, sparks flying from a knife grinder’s wheel.</p>
<p class="P26">Even if Zuccotti was barren in winter as I wrote this, rarely did a day pass
            without mention in the media of OWS or the huge gap between the rich and the rest. The
            day I passed Zuccotti Park in mid-January, the mothers of girls in private schools of
            the Upper East Side, like Spence and Brearley, were reported in the <em>New York
                Times</em> as bemoaning the fact that, for the first time ever, their daughters had
            not gotten into Yale on early admission. Was that because of anger at the 1 percent?
            they wondered. That same day I passed a hole-in-the-wall restaurant uptown on Amsterdam
            Avenue and 102nd Street called Busters of New York. On a blackboard outside was
            displayed its menu:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong><a id="page_16"></a>Wrap: “Occupy the
            Dream”</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Down the street from Zuccotti Park, the Museum of the American Indian. Right
            by the park, an African slave burial ground. How extraordinary! And right here in
            Zuccotti Park, many black protesters. But amid the hustle and bustle of the streets,
            does anyone notice that the center of the world’s money—what makes this
            city so “global”—rests on top of skeletons of African slaves and
            ghosts of Indians, no doubt shaking wampum and featherwork. It is all so arty now, like
            Julie Mehretu’s gorgeous five-million-dollar, eighty-foot-long mural adorning the
            glass-walled lobby of the new Goldman Sachs building along the Hudson, not so far away.
            They so want art, the 1 percent. Man does not live by bread alone. And art is a
            great—many say the greatest—investment in these troubled times. Three days
            before the occupation is forcibly ended by the baton-wielding NYPD, art shows its
            power:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>As Stocks Fall<br/> Art Surges at a $315 Million Sale</strong></p>
<p class="line1">.   .   .</p>
<p class="C182t"><span class="size_90"><em>Despite (or perhaps because of) the stock
                    market’s nearly 400 point plunge on Wednesday, collectors on Wednesday
                    night raced to put their available cash–and lots of it—into
                    art<br/>
</em>(<em>New York Times</em>, November 10, 2011)</span></p>
<p class="P26">But try to be a young artist impassioned by art—something you could
            die for—if you don’t have a trust fund and your parents aren’t rich
            with connections in the art world. I dare you. The humiliation. The slime. The eating
            away of self-confidence. Do anything, anything at all, to survive. The heart-rending
            questions: What is art? Why that, and not this?</p>
<p class="C164"><a id="page_17"></a><img alt="" class="Image83" src="img/34_1.png"/></p>
<p class="P26">A little further uptown, where prostitutes practiced their art and hoary
            truckers got laid, where the smell of rancid fat from the meat packing district used to
            be, now you have the lovely “high line” of swaying grasses along the
            abandoned railway tracks, the capstone of gentrification from which you can peer down
            into boutique stores and forget that Manhattan has become unlivable for most people.
            What are all those smart people talking about in those chic restaurants?
            “Ultimately what Zuccotti Park is all about,” Reinhold tells me (and he
            should know, urban planner that he is), “is real estate.” What he means is
            that the occupation is testing the limits of monetarized space. So what we have is</p>
<p class="C182"><span class="size_90">real estate<br/> finance capital<br/> art<br/> and
                now OWS (another form of art)</span></p>
<p class="P26">Man does not live by bread alone. They so need art, the 1 percent. <em>But so
                does OWS.</em> This is not only a struggle about income <a id="page_18"></a>disparity and corporate control of democracy. It is about the practice of art, too,
            including the art of being alive.</p>
<p class="P26">History congeals, then dissolves; and somehow art always ends up being art.
            When some OWS-inspired people dropped a banner inside the Museum of Modern Art in
            support of the art handlers locked out of Sotheby’s, MOMA people quickly
            appropriated the banner as art.</p>
<p class="P26">History congeals, then dissolves. The chiseled stone of the older Wall Street
            buildings gives way to mirrored buildings fighting free of history on postmodern wings.
            Money helps. Night and day, the crescendo of jackhammers obliterates time itself. Cranes
            lace the sky, adding new constellations. “All that is solid melts into
            air.” <em>The Communist Manifesto.</em> Marshall Berman, “the bourgeoisie
            has a vested interest in destruction.” But one day it will go too far. Marx and
            the Wobblies, giving birth to the new society in the womb of the old. Dreams of the
            classless society. Tomb and womb. Space of death. Indians with the ghost dance. Starting
            up again. “Fellow slave” is how the Wobblies addressed each other. Fellow
            slave. A sign on the pavement:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Nobody is more hopelessly enslaved than<br/> those who believe
                they are free</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">I look in heaps of garbage for plastic bags to cover us if we try to get some
            sleep. Huge white plastic bags outside Starbucks look usable. Homeless woman asleep in a
            doorway, wrapped in a enormous black plastic bag. Right idea. Slight drizzle. Warm. Get
            to the park. A crazy-looking guy walks by with a sign:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>We are the future<br/> We are going to win</strong></p>
<p class="line1">.   .   .</p>
<p class="P1"><a id="page_19"></a>He is dragging a white dog. He is ready to fight, but his
            forked fingers mean peace. Some people are ripping open plastic bags. The “human
            microphone,” which everyone spells as “mic check” but is pronounced
            “mike check,” is in full swing, explaining civil disobedience and what to
            do when arrested. I hook up with my students and with Saa. Magic markers are passed
            around, for writing the telephone number of the Lawyers’ Guild on one’s
            skin. Rain is getting heavier. We are being encouraged to clean the park, which seems
            absurd to me, because that validates the mayor’s excuse for dealing with
            protesters, as vermin that need extermination . . . time and again, the
            unclean, the disorderly, the un-uniformed, the un-uniform. And let’s not forget
            the worst, the anarchists, as much vilified by the police as by Marx and Engels.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>We all know<br/> where the real dirt is</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">“It has to be cleaned up,” the “chief executive”
            (note the nomenclature) of the management company overseeing the park is reported as
            saying. The billionaire mayor’s girlfriend is on the board of the company that
            owns the park, and the mayor (according to the <em>New York Times</em>, October 15,
            2011) “is a mayor obsessed with the cleanliness of the city’s public
            spaces.” Later we hear that the management company is way behind in paying its
            taxes to the city. There are brooms and soap galore, and here I am with a broom,
            side-by-side with a merry fellow in a Santa Claus outfit leading the crew. A woman
            starts up a mic check.</p>
<p class="C182b"><span class="size_90">Hello<br/> Hello</span></p>
<p class="C182_25">I am the sanitation group <br/><a id="page_20"></a>I am the sanitation
            group</p>
<p class="C182_25">(Her voice is shrill, authoritative, nagging)</p>
<p class="C182c"><span class="size_90">I am not <em>the</em> leader<br/> I am not
                    <em>the</em> leader</span></p>
<p class="C182_25">(long pause)</p>
<p> </p>
<p class="C182cb"><span class="size_90">I am <em>a</em> leader<br/> I am <em>a</em>
                leader</span></p>
<p class="P26">The park slopes downhill to the west. Rivers of soapsuds float west merrily
            along with Santa. Saa loves to clean, and is doing a great job. It feels good to be
            doing something physical. There are many brooms, all new. No shortage of stuff in
            Zuccotti Park. This place is organized! Check out the People’s Library, the
            kitchen, The Poets’ Corner, the drummers, and the altar. But no time for that
            now. We are in lockdown, as if a hurricane is imminent. The three thousand or more
            library books are lovingly bundled into plastic boxes. Together with the poets, the
            books are the “crown jewels” of this liberated zone, this experiment in
            “horizontal” decision-making and vertiginous imagination. When the
            occupation is finally and spectacularly smashed a month later, during the night of
            November 15, with riot police beating up protesters and journalists alike, and the night
            sky humming with police helicopters, the books are thrown into garbage cans and taken
            away, supposedly to the sanitation garage on 57th Street. Can you imagine!
                <em>Sanitation</em> for books! (And why can’t they say <em>garbage</em> in
            this country?) Not to worry.</p>
<p class="P26">“Every morning before GA [the general assembly], we would gather on
            the street and start up the drums,” says Danny. “Our efforts channeled the
            pulse of the occupation.” On the first night of occupation he felt drawn to the
            group of people drumming, singing, and dancing. He had never thought of himself as a
            musician or a performer, but he felt compelled to pick up a small <a id="page_21"></a>drum. “As we share this warm harmony, “ he later wrote, “I
            decide to burn some incense. It seems others had something similar in mind and soon we
            are enveloped in candles, smoke, and warmth. While many of us play, a few souls decide
            to stand up and channel the rhythms into song and rhyme. Out of nowhere these wonderful
            lyrics emerge full of love, dreams, and longing for the moment of revolution. The space
            upon which we play is consecrated and transformed.”</p>
<p class="P26">Next day, the people in the park invented mic check, originally “the
            people’s microphone”—and Danny found himself facilitating the
            speakers through the process that has come to be called “stacking,”
            whereby your eye is caught by someone in the crowd and you place them on the list. You
            learn the hand signals quickly down there, and invent just as quickly. Like the mic
            check, these signals bear the mark of an exotic tribe and secret society, invented
            yesterday, brushing history against the grain.</p>
<p class="P26">It is said that the mic check was invented because of the city ban on
            microphones in Zuccotti Park. That ban triggered the most powerful invention of the
            Occupy movement.</p>
<p class="P26">Seven months later in Union Square, at the New York May Day march, I saw many
            of the people who had been in Zuccotti Park, but this time there was something wrong.
            Instead of the magic of the mic check, there was a powerful public address system
            dominated by one or two people screaming slogans. There was little chance for the rest
            of us to converse with each other. The casual atmosphere was gone, as was the chance to
            hear opposing points of view enjoined by the crowd repeating the speaker as with the mic
            check. In that situation you rarely felt you were being screamed at or lectured. The
            easiest way to kill the Occupy movement would be with a centralized public address
            system, as opposed to the rippling network of wildlife that was Zuccotti.</p>
<p class="P26">The weather was unusually balmy in Zuccotti Park in September and October. To
            visit the park was like going to a street fair.</p>
<p class="C160" style="page-break-before:always;"><a id="page_22"></a><img alt="" class="Image83" src="img/39_1.png"/></p>
<p class="C160" style="page-break-before:always;"><img alt="" class="Image87" src="img/39_2.png"/></p>
<p class="C160" style="page-break-before:always;"><a id="page_23"></a><img alt="" class="Image83" src="img/40_1.png"/></p>
<p class="C160" style="page-break-before:always;"><a id="page_24"></a><img alt="" class="Image83" src="img/41_1.png"/></p>
<p class="C164" style="page-break-before:always;"><img alt="" class="Image87" src="img/41_2.png"/></p>
<p class="P26" style="page-break-before:always;"><a id="page_25"></a>There were so many smiling, radiant people, mixed with a
            few grim, concentrated ones. Some women were topless. Many people were on their hands
            and knees making signs on brown cardboard recycled from boxes. T-shirts were being
            silk-screened. As the days went by, older people got into the mix. On the pavement by
            the park, tourist photographers stood three deep, many engaged in polite but strenuous
            political, philosophical, and theological debate.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Mutual responsibility<br/> Come chat with us</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">The park was ablaze with flags: rainbows, the planet earth, and of course Old
            Glory with the logos of corporations instead of stars. Poor stars, trumped like this.
            But the trees still had their leaves, fluttering.</p>
<p class="P26">Most of all, I was struck by the statuesque quality of many of the people
            holding up their handmade signs: like centaurs, half-person, half-sign. Looking now at
            the photographs, which <a id="page_26"></a>give me some distance from the hurly-burly of
            the face-to-face realities, I see the sign as an extension of the human figure, that
            history is being made by this stiller-than-still conjunction, heavy with the weight of
            ages and the exhilaration of bucking the system. And then I realize that this
            centaur-like quality and stiller than stillness—this terrible
            gravitas—occurs because the sign holder is posing for photographers, or rather,
            because the sign is being made to pose for the camera with its very stillness <a id="page_27"></a>calling to mind—for the aficionados, at least—that
            wonderful line of Adorno’s in which he tells us that the trick to
            Benjamin’s style is the need to become a thing in order to break the magic spell
            of things. Compare the statuesque quality of the centaurs with the radiance of the sign
            come alive.</p>
<p class="C160"><img alt="" class="Image83" src="img/42_1.png"/></p>
<p class="C164"><img alt="" class="Image83" src="img/43_1.png"/></p>
<p class="P26">It is the handmadeness of the signs, their artisanal crudity, art before the
            age of mechanical and digital reproduction, that facilitates this hop, skip, and jump.
            To Nancy Goldring, who took many of the photographs accompanying this essay, it <a id="page_28"></a>seems as if this graven quality comes from the sign saying exactly
            what the sign bearer wants to say. Put another way, the sign has a talismanic function,
            an incantatory drive, and is of divine inspiration, the gods in this case being of
            mirthful disposition, feeling quite at home in the park.</p>
<p class="C160"><img alt="" class="Image83" src="img/44_1.png"/></p>
<p class="C164"><img alt="" class="Image83" src="img/45_1.png"/></p>
<p class="P26">For a century now, advertising signs and images have stolen from the
            avant-garde. Now it’s payback.</p>
<p class="C164"><a id="page_29"></a><img alt="" class="Image83" src="img/46_1.png"/></p>
<p class="P26">And here we are on the night of October 13, when the city is going to bring
            in its sanitation workers, backed by police. The tents have come down.</p>
<p class="P26">The park has become a sea of blue tarpaulins glistening with rain, a sea of
            hope. Is this the sea that Mao talked about in his writings on guerrilla warfare? Are we
            the waves to which Nietzsche passionately refers in his “Will and Wave,”
            that mighty turbulence breaking on the rocks of time, dashing pearls, holding court with
            history through secret affinities?</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>That is how the waves live—<br/> that is how we live,<br/>
                we who will—I will say no more</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Zuccotti Park is all that, bathed in an unearthly yellow-green light coming
            from the streets around. Underneath, hard granite. And underneath that? The beach!</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong><a id="page_30"></a>Truly, at this moment nothing<br/> remains of
                the world but green<br/> dusk and green thunderbolts</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Like dancers we swirl, floating on high spirits and the sense, no matter how
            silly, that at least we are accomplishing something by cleaning. Some socks float by.
            There is a smell of sage burning. Shamans circle the perimeter of the park providing the
            real cleansing. Scrub away. How absurd!</p>
<p class="P26">We use our magic to thwart their magic. They have pepper spray. We have
            burning sage. They prohibit microphones. We have the people’s microphone. They
            prohibit tents. We improvise tents that are not tents but what nomads used before North
            Face. They build buildings higher than Egyptian pyramids, but that allows our drumming
            to reverberate all the louder and our projections of images and e-mails at night to be
            all the more visible and magical, taking advantage of the megascreens that the facades
            of these giant buildings provide.</p>
<p class="C164"><img alt="" class="Image83" src="img/47_1.png"/></p>
<p class="P26"><a id="page_31"></a>Each day, each week, sees another deterritorialization of
            their reterritorializatons. They prohibit the electric generators we use for our
            computers and cell phones. We set up bicycles that can generate power, and people who
            would otherwise gawk and take photographs get into the movement; they become Dionysian
            and not just Apollonian, sitting in the saddle and pedaling like crazy. This is how they
            get into the movement. One woman sees it in historical terms, running in matrilines. As
            she pedals, smiling, she says, “I can tell my grandchildren I provided energy for
            Occupy Wall Street.”</p>
<p class="C164"><img alt="" class="Image83" src="img/48_1.png"/></p>
<p class="P26">By her side, several older women sit sedate in lounge chairs knitting woolies
            for OWS and the coming winter. They have all the time in the world, for they inhabit
            time and time stands still. They don’t need to reference history or the
            matriline. They are all that with what Benjamin called “the time of the
            now,” that compressed stasis which is the revolutionary moment. Clickety-clack go
            the knitting needles as history is rewoven. They have cardboard signs by their sides,
            voicing their outrage. Clickety-clack. This is not the clickety-clack of the locomotive
            of history <a id="page_32"></a>which Marx invokes in his preface to <em>The Introduction
                to The Critique of Political Economy</em>. This is not the clickety-clack of
            Benjamin in his anarchist (Blanqui) mode, trying to figure out when to pull the
            emergency brake that will usher in the revolution. Nor is it the explosion that Benjamin
            invokes as the blasting apart of the continuum of history that creates the
                <em>jetztzeit</em>, the “time filled with the presence of the now.”
            Revolution is different now.</p>
<p class="C164"><img alt="" class="Image83" src="img/49_1.png"/></p>
<p class="P26">Another vision of revolution surfaces: a cheeky little 4×3”
            sticker adorning the seventy-foot-high orange metal <em>Joie de Vivre</em> sculpture at
            the southeast corner of the park:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Lick my<br/> Goldman<br/> Sachs!</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Jack the giant-killer! This little fellow transforms the sculpture, another of
            New York’s notable contributions to public art, but that is nothing compared with
            what has been going on in <a id="page_33"></a>the park since OWS, where art acquires new
            meaning as we read here:</p>
<p class="C160"><img alt="" class="Image83" src="img/50_1.png"/></p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>This<br/> revolution for<br/> display<br/> purposes<br/>
                only</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">In the corner of the diminutive sticker, we read:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>My money’s no whore 99%</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">On all sides jackhammers, police sirens, and traffic roaring down Broadway,
            echoes bouncing off buildings. Mic check and the poets keep at it, along with the drums.
            Mic check: I still can’t get over this crazy spelling, a code for the initiated.
            An assault <a id="page_34"></a>on the signifier. My own name sometimes gets spelled
            thus: Mic. So what? Mick check? What is being checked here, anyway? A mic check is a
            check to ensure that an electronic sound system is functioning. <em>Doesn’t
                matter what the sound is. Right?</em> Or rather, that the channel is open to all
            sounds. Everyone can have a shot. If anything is emblematic of the movement, this is it.
            And what of the marvelous paradox that this was invented in Zuccotti Park because
            microphones are banned by law! Inadvertently, what we can call “the
            system,” the nervously nervous nervous system (which is also the blue tarpaulin
            sea), spun Hegel off his feet and gave the dialectic of history another twist,
            converting the state prohibition into a magical weapon that is now being copied across
            the country, if not the world, as a new form of social speech, a new form of being.
            Michelle tells me that even the police tried to use it once—at the end of the #3
            subway line at New Lots, where OWS occupied a foreclosed home. Everyone laughs.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>I think they got it wrong. We’ve been occupied<br/> for
                years. This occupation is de-occupation</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">And why magic? Because of the repetition? In repetition you come to grips with
            trauma, as will be recognized by readers of “Beyond the Pleasure
            Principle,” a text that in Zuccotti, with “mic check,” becomes a
            living text better called “Beyond ‘Beyond the Pleasure
                Principle,’<span class="sfs"></span>” since here death and pleasure
            commingle in the exploration of the creative effect of shock in modernity, there being
            different types of shock at work in OWS.</p>
<p class="P26">The shock of the system imploding (depression/recession) is one of these
            shocks. The shock of mounting a challenge (OWS) is quite another, and both types of
            shock galvanize body and imagination. Shock speeds things up faster than light, but it
            also slows time down to dead crawl. This is the essence of Zuccotti as <a id="page_35"></a>people invent new ways of being, new ways of talking to one another. This is what
            makes for the “trance” Danny talked about. But it is also an
            awakening.</p>
<p class="P26">“Beyond the Pleasure Principle” is an essay that starts with
            shock and ends with the psyche become inorganic matter. History becomes natural history,
            yet natural history carries its own cosmic charge, as when the tides turn with the
            ascent of the moon and the sorcerer’s spell gains momentum with song. And what of
            the driving storm tonight, the clashing elements as the mayor prepares his NYPD! For
            what is the death drive but a marvelous giving up, a succumbing—all that
            negativity which our culture tells us is wrongheaded and unhelpful—yet it is
            surely part of the libidinal energy that goes into this rebellion against money (see
            Norman O. Brown, <em>Life against Death</em>)? This is the physicality of the natural
            history at work in Zuccotti Park, the secret of what Salomeya calls “erotic
            materialism.”</p>
<p class="P26">And why magic? Because with mic check, the rhetorical style alters towards
            the fundamental, the pithy, and the word jab. These are shocks too. These are drumbeats
            of words like the drums that keep going at the west side of the park, where what people
            call “the right brain” functions in contrast and as complementary to
            “the left brain” on the east side of the park (the higher side, of
            course), where mic checkers get their chance, and where the “general
            assemblies” take place every day.</p>
<p class="P26">And why magic? Because there is a religious wave, an African and/or
            African-American thing going on here, with the repetition of the pastor’s words
            by the listeners, who are plural. We hear together. We repeat together. And in that
            repetition we first hear, then speak, thereby tasting the words in our mouths like
            cherries, with time to let the ideas settle. The idea part of the spoken word gets its
            chance to resonate in different dimensions of thought and of feeling.</p>
<p class="P26">There can be fear here, too: fear of mindless repetition of the brainwashed.
            This is the cultic expression of magic. But then the next mic checker gets up, and the
            message is quite different from <a id="page_36"></a>the last one! We repeat, yet we
            transform. And think of Allen Ginsberg with his harmonium and his <em>OOM</em>,
                <em>OOM,</em> and many more <em>OOM</em>s. There is joy in spoofing the too-serious
            ones, left-wing or right-wing, who assume they are the ones who know and have come here
            to tell us what’s what. Remember Debs, the ultimate seduction? “I will not
            lead you into the Promised Land because if I do, others can lead you out.”</p>
<p class="P26">This is worth thinking about. Occupying Wall Street inevitably means
            occupying how we talk in public, how we learn and teach, and how we write ethnography
            (about our own tribe). All this is up for grabs. Otherwise there is no occupation.</p>
<p class="P26">The occupation is self-reflexive to a fault.</p>
<p class="P26">True. But we are also the nervy nervous ones awaiting attack. In the wan,
            green light our sea ripples over the shoals of the nervous system. We are awaiting the
            police, who at the moment form a blue cordon around two sides of the park. On the north
            side their blue-and-white cars and vans face us, ready to leap. Behind the cars stands a
            skyscraper crisscrossed by dark steel beams, like a fortress. An art student from
            Parsons tells me it is the FBI building. In bold black letters on the facade, as I
            recall:</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>One Liberty Plaza</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">By 2 a.m. it is pouring. There is lightning and thunder. The heavens erupt.
            Reality mimics art, by which I mean John Cage’s 1976 performance <em>Lecture on
                the Weather</em>, in which art mimics reality, the reality of storm. Cage devised
            this performance for the Canadian Broadcasting Corporation, to mark the US bicentenary.
            Twelve speakers recite different passages selected by chance from Thoreau’s
                <em>Civil Disobedience</em>, all at the same time, until, about half an hour later,
            with a swelling hubbub of voices, lightning flashes and thunderclaps bring history and
            natural history together.</p>
<p class="P26"><a id="page_37"></a>The crowd has thinned. The commercial food stands on the
            south side of the park are doing a great business. Their warmth, light, and sense of
            bounty are cheering. I meet up with students from Columbia. We mix and mingle, talking,
            smoking, animated, searching for shelter, waiting for the bust, scenarios of the
            impending attack running through our minds. Many people prefer to stand, shifting from
            one foot to another, finding friends, meeting strangers, forming a strange new community
            of the righteous and the beleagured. Classic concepts leap to mind—especially the
            “liminal space” Victor Turner illuminated for us from his studies in
            southern Africa in the 1950s, where the initiates were gathered and transformed step by
            step through rituals and symbols resonant with myth. Being betwixt and between, the
            initiates occupy a magical space in which the elementary forms of religious life take
            fire. The twin poles of birth and death frame the space. Womb and tomb.</p>
<p class="P26">“I asked my friend why he always wears that scarf,” says Danny.
            “He’s wearing the scarf that he wore on the first night of the occupation.
            I ask him why he always carries that scarf around him wherever he goes. He tells me
            it’s like his baby blanket. ‘On September 17 I died. On September 17 I
            began living. I found this scarf that day. It is a relic from the rebirth, from the
            moment when I started taking my first steps in this new life, in this new
                me.’<span class="sfs"></span>”</p>
<p class="P26">Tomb and womb. A child is born from the womb just as spirits emerge from the
            tomb. <em>Emergence</em> is the trope. Emergence from the underworld. Orpheus,
            don’t look back! Not this time! Sing your songs so beautiful they enchant animals
            and things such as these buildings reaching the sky, this Wall Street so mythical and
            world-dominating. “Mr. Gorbachev, tear down this wall,” said the Great
            Communicator.</p>
<p class="P26">But what have we here? Weeks later, I see a photograph of a tall young man
            with camouflage pants, a black cowboy hat, and a big smile. He is standing on the
            perimeter of the park along Broadway, with a young girl on his shoulders who is holding
            up <a id="page_38"></a>a sign. Her mouth is wide open. Is it surprise? Is she
            challenging us? As always, the faces of the people holding the signs say as much as the
            signs themselves, and the signs say plenty. What is she holding up?</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Mr. Obama<br/> tear down<br/> this wall</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Looking for a new language? Well, here it is: the language of the sign, the
            language of wit rearranging history through a barrage of A-Effekts that warp what we
            have taken for real. It is a scene-language, like that on Brecht’s placards hung
            above the stage. “Mr. Obama, tear down this wall!”</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>I’m so angry that<br/> I made a sign</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">There is no doubt a morality play is taking place here, powered by the
            “moral economy” that the historian E. P. Thompson brought to our
            attention. This is even bigger than terms like “growing income disparity”
            or “We are the 99 percent” convey—terms encumbered by the same
            economism they challenge. “The rupture between bodies and homes, the rupture of
            foreclosure,” writes Michelle, whose own family was foreclosed in Los Angeles,
            “is revelatory—spirits of home emerge as well as the specters of power.
            The taking of a home by a bank is experienced as a violation of sacred space.”
            Thompson analyzed eighteenth-century bread riots in London. Today it’s homes.</p>
<p class="P26">The Global City we hear so much about gets its comeuppance. There was the
            rancor of 9/11 as the empire struck back <a id="page_39"></a>a few blocks from here. And
            ten years later, there was Tahrir Square, downtown Cairo. Peter Lamborn Wilson put his
            pen and paints to work to make a poster which on April 20, 2011, he mailed from Cairo, a
            lamentably poor town in upstate New York. He mailed copies to the Gallery of the
            Surrealist Movement in Cairo, to the Egyptian National Museum, and to Egyptians in the
            United States, asking them to send it on to friends in Cairo. He also mailed a few to
            people like me.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Wake up America<br/> Be like Egypt<br/> Fatimid Order of Cairo,
                NY, greets Cairo,<br/> Egypt, Tahrir Square<br/> Overthrow all pharaohs<br/>
                Power to the world of the imagination<br/> Be-sphinxed</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Monstrous masks and transgression are de rigueur in the liminal period. In OWS
            this is manifest in outrage transformed automatically into humor and play, and likewise
            by the NYPD in its growl, pepper spray, and medieval riot gear. The atomized mass of
            yesterday, without hope, has crystallized into a community defining itself through a new
            language and sense of collective. It is a movement that seems to have come out of
            nowhere, a messianic movement after the death of God that kindles our polymorphously
            perverse infancy with relish. More than anything else it is an attitude, a mood, an
            atmosphere, like John Cage’s babbly lecture on weather mixing up Thoreau’s
                <em>Civil Disobedience</em> with thunder and lightning, and this is why the
            politicians and the experts have a problem. They see OWS as primitive and diffuse
            because it has no precise demands—as if the demand for equality were not a
            demand, at once moral and economic, redefining personhood and reality itself. OWS <a id="page_40"></a>is akin to the “primitive rebels” that Eric Hobsbawm
            called the anarchists of Spain, a movement he saw as “pre-political.” What
            the experts want is for OWS to submit to the language of the prevailing system. Yet is
            it not the case that merely to articulate such is to sell out the movement? There is as
            yet no language to express the “drift,” as Lyotard called it, with
            ’68 in mind. Politics as aesthetics is back. Politics as “affective
            intensity” is back too. “A successful attack on the belief in necessity
            would inevitably lead to the destruction of kapital’s very main-spring,”
            he wrote back then. The laws of equivalence are in suspense, and libidinal impulses are
            unhooked from prevailing norms. But the experts want to channel the messianic and
            transgressive impulse into their own need for pathological fame and power.</p>
<p class="C164"><img alt="" class="Image83" src="img/57_1.png"/></p>
<p class="P26">Saa finds a granite step that can serve as a backrest for sleeping while
            sitting. The plastic keeps off some of the rain, but everything is wet now. We doze,
            flitting between sleep and waking, bathed in that eerie green-yellow light that makes
            the sea of tarpaulins glisten, while beneath are bodies forming waves and <a id="page_41"></a>hillocks, like seals on the beach, vague outlines of animated
            beings pushing through. Is that our movement? Is that OWS, with its eerie green-yellow
            light and glistening tarp surfaces, with animal-like shapes pushing through? On the
            south side of the park where we lie, the rain beats down with demonic fury, and a crude
            sign in large white letters stands out above the police barriers: “Liberty and
            justice for all.”</p>
<p class="C144">5:50 a.m.: sudden mic check. “Breaking news” (funny how they
            reproduce the media, especially at this crucial moment). The park has filled to
            overflowing in the past dark hour, along with rising tension, and three
            “echoes” or rebootings of the mic check are required to get to the people
            at the back.</p>
<p class="C185_26">Breaking news</p>
<p class="C185_26">Breaking news</p>
<p class="C185_26">Breaking news</p>
<p class="C146">The human microphone is bursting to capacity. Echoes chase echoes, and only
            the most alert ears and powerful voices are able to transmit anything. Hope and fear
            blur the message. The faces in the sea of faces around me in the magic hour of dawn are
            faces of angels and trust. We bond. We embrace with our eyes. You strain forward, then
            pivot 180 degrees to catch the repetition. We feel the incredible power of
            repetition—each repetition the same, each one slightly off. (“What did
            they say?”) It could be natural forces: that sea, those waves again, that muffled
            thunder, on our side, now, natural force, natural history.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>The deputy mayor has canceled the cleanup.</strong></p>
<p class="line1">.   .   .</p>
<p class="C185">For a second a stunned silence</p>
<p class="C185">Then cheers of unbelievable elation</p>
<p class="C185">A young man asks for a mic check</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong><a id="page_42"></a>Look up<br/> Look up<br/> Look
            up</strong></p>
<p class="pullx"><strong>See the sky<br/> See the sky<br/> See the sky</strong></p>
<p class="pullx"><strong>A new dawn<br/> A new dawn<br/> A new dawn</strong></p>
<p class="line1">.   .   .</p>
<p class="P1">Mist clings to the skyscrapers. The mottled sky grows pink with promise of
            light. My sun, ’tis of thee. For a glorious moment, history and natural history
            fuse. Second nature dissolves. The time of the now.</p>
<p class="line">.   .   .</p>
<p class="pullx"><strong>Clickety clack<br/> Clickety clack<br/> Clickety
            clack</strong></p>
<p class="line1">.   .   .</p>
<p class="C151p">The end</p>
<p class="C160"><img alt="" class="Image85" src="img/59_1.png"/><a id="page_43"></a></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_I__M_SO.vue?9fca4e7a